import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError, uploadFile, downloadSigned, ocrScan } from "@/lib/api";
import { formatCZK, formatDate, currencySymbol } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/shared/PagePrimitives";
import { ReceiptText, Plus, Upload, FileText, CheckCircle2, Clock, XCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useDocPicker, isPhone } from "@/components/shared/DocPicker";

const STATUS = {
  pending: { cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400", label: "čeká na schválení", Icon: Clock },
  approved: { cls: "bg-success/15 text-success", label: "schváleno", Icon: CheckCircle2 },
  rejected: { cls: "bg-destructive/15 text-destructive", label: "zamítnuto", Icon: XCircle },
};

export default function MyInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", note: "", suggested_project_id: "" });
  const [fileName, setFileName] = useState("");
  const [fileUrl, setFileUrl] = useState("");   // nahraný soubor (upload proběhne hned při výběru)
  const [ocr, setOcr] = useState(null);          // OCR přepis faktury
  const [ocring, setOcring] = useState(false);
  const [saving, setSaving] = useState(false);
  // 184: „Nahrát fakturu" rovnou otevře výběr fotky / PDF; formulář se ukáže až s vybraným souborem.
  const docPicker = useDocPicker({ onPick: (f) => { setOpen(true); onFile({ target: { files: [f] } }); } });

  const load = useCallback(() => {
    api.get("/sub-invoices").then((r) => setInvoices(r.data)).catch(() => {});
    api.get("/projects/options").then((r) => setProjects(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // 202: „Nahrát fakturu“ z úvodu na telefonu (/moje-faktury?nahrat=1) rovnou otevře skener
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get("nahrat") !== "1") return;
    const n = new URLSearchParams(params); n.delete("nahrat"); setParams(n, { replace: true });
    if (isPhone()) docPicker.open("faktura");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const resetForm = () => {
    setForm({ amount: "", note: "", suggested_project_id: "" });
    setFileName(""); setFileUrl(""); setOcr(null);
  };

  // Výběr souboru -> nahrát a hned spustit OCR (na pozadí, s pollingem).
  // Po načtení se předvyplní částka a uloží celý přepis (dodavatel, DPH, položky).
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name); setOcr(null); setOcring(true);
    try {
      const url = await uploadFile(f);
      setFileUrl(url);
      const data = await ocrScan("sub-invoice", url);
      setOcr(data);
      const amount = Number(data.total_with_vat) || Number(data.total_without_vat) || 0;
      if (amount > 0) setForm((s) => ({ ...s, amount: String(amount) }));
    } catch (err) {
      // OCR selhalo — soubor je nahraný, částku vyplní subdodavatel ručně.
      toast.error(err.response?.status === 422
        ? "Údaje z faktury se nepodařilo načíst. Zadejte částku ručně."
        : (formatApiError(err.response?.data?.detail) || "Načtení faktury selhalo."));
    } finally { setOcring(false); if (e.target && "value" in e.target) e.target.value = ""; }
  };

  const submit = async () => {
    if (!fileUrl) return toast.error("Přiložte soubor faktury (foto nebo PDF).");
    if (ocring) return toast.error("Počkejte na načtení faktury.");
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Zadejte částku faktury.");
    setSaving(true);
    try {
      await api.post("/sub-invoices", {
        file_url: fileUrl, amount: Number(form.amount), note: form.note || null,
        suggested_project_id: form.suggested_project_id || null, ocr: ocr || null,
      });
      toast.success("Faktura odeslána ke schválení.");
      setOpen(false); resetForm();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Nahrání selhalo."); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="my-invoices-page">
      {docPicker.inputs}
      <PageHeader title="Moje faktury"
        actions={<Button onClick={() => docPicker.open("faktura")} data-testid="invoice-add-btn" className="h-11 font-bold"><Plus size={16} /> Nahrát fakturu</Button>} />

      {invoices.length === 0 ? (
        <div className="bg-card border border-border rounded-[20px] p-8 text-center text-muted-foreground">
          <ReceiptText size={32} className="mx-auto mb-3 opacity-50" />
          Zatím žádné faktury. Nahrajte fakturu za odvedenou práci — stavbyvedoucí ji schválí a přiřadí k zakázce.
        </div>
      ) : (
        <div className="space-y-3">
          {invoices.map((inv) => {
            const st = STATUS[inv.status] || STATUS.pending;
            return (
              <div key={inv.id} data-testid={`invoice-row-${inv.id}`}
                className="bg-card border border-border rounded-[20px] p-4 flex items-center gap-4 flex-wrap">
                <button onClick={() => downloadSigned(inv.file_url, `faktura-${formatDate(inv.created_at)}`)}
                  className="flex items-center gap-3 flex-1 min-w-0 text-left" data-testid={`invoice-open-${inv.id}`}>
                  <FileText size={20} className="text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <div className="font-bold tabular-nums">{formatCZK(inv.amount)}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      nahráno {formatDate(inv.created_at)}
                      {inv.status === "approved" && inv.project_name ? ` · zakázka ${inv.project_name}` : ""}
                      {inv.status === "pending" && inv.suggested_project_name ? ` · návrh: ${inv.suggested_project_name}` : ""}
                    </div>
                    {inv.status === "rejected" && inv.rejected_reason && (
                      <div className="text-xs text-destructive mt-0.5">Důvod: {inv.rejected_reason}</div>
                    )}
                  </div>
                </button>
                <span data-testid={`invoice-status-${inv.id}`}
                  className={`text-xs px-2.5 py-1 rounded-full font-semibold shrink-0 inline-flex items-center gap-1 ${st.cls}`}>
                  <st.Icon size={12} /> {st.label}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nahrát fakturu</DialogTitle><DialogDescription className="sr-only">Nahrání faktury subdodavatele</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Soubor faktury</Label>
              <button type="button" onClick={() => docPicker.open("faktura")} disabled={ocring} data-testid="invoice-file-label"
                className={`mt-1 w-full flex items-center gap-2 h-11 px-3 rounded-[14px] border border-dashed text-left transition-colors ${ocring ? "border-brand/40 opacity-70 pointer-events-none" : "border-border hover:border-brand"}`}>
                <Upload size={16} className="text-muted-foreground shrink-0" />
                <span className="text-sm truncate">{fileName || "Vybrat fakturu (foto nebo PDF)"}</span>
              </button>
            </div>

            {ocring && (
              <div className="flex flex-col items-center justify-center gap-3 py-6 text-center rounded-[14px] border border-border bg-muted/30" data-testid="invoice-ocr-loading">
                <div className="w-9 h-9 rounded-full border-4 border-secondary border-t-brand animate-spin motion-reduce:animate-none" />
                <div className="text-sm font-semibold">Načítám fakturu…</div>
                <div className="text-xs text-muted-foreground">OCR vyčítá částku a položky, chvíli to potrvá</div>
              </div>
            )}

            {ocr && !ocring && !(ocr.supplier_name || Number(ocr.total_with_vat) > 0 || Number(ocr.total_without_vat) > 0) && (
              <div className="rounded-[14px] border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-600 dark:text-amber-400" data-testid="invoice-ocr-empty">
                Údaje z faktury se nepodařilo načíst. Zadejte částku ručně níže.
              </div>
            )}

            {ocr && !ocring && (ocr.supplier_name || Number(ocr.total_with_vat) > 0 || Number(ocr.total_without_vat) > 0) && (
              <div className="rounded-[14px] border border-brand/30 bg-brand/5 p-3 text-sm" data-testid="invoice-ocr-summary">
                <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-brand mb-1.5"><Sparkles size={13} /> Načteno z faktury</div>
                {ocr.supplier_name && <div className="font-semibold truncate">{ocr.supplier_name}</div>}
                <div className="mt-1 space-y-0.5 text-muted-foreground tabular-nums">
                  {Number(ocr.total_without_vat) > 0 && <div className="flex justify-between"><span>Bez DPH</span><span>{formatCZK(Number(ocr.total_without_vat))}</span></div>}
                  {Number(ocr.vat_amount) > 0 && <div className="flex justify-between"><span>DPH</span><span>{formatCZK(Number(ocr.vat_amount))}</span></div>}
                  {Number(ocr.total_with_vat) > 0 && <div className="flex justify-between font-semibold text-foreground"><span>Celkem</span><span>{formatCZK(Number(ocr.total_with_vat))}</span></div>}
                  {(ocr.line_items || []).length > 0 && <div className="text-xs pt-0.5">{ocr.line_items.length} položek — rozdělení provede stavbyvedoucí</div>}
                </div>
              </div>
            )}

            <div>
              <Label>Částka k úhradě ({currencySymbol()})</Label>
              <Input type="number" min={0} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="h-11 mt-1 w-full min-w-0" data-testid="invoice-amount" placeholder={ocring ? "Načítám…" : "0"} />
              <p className="text-[11px] text-muted-foreground mt-1">Předvyplněno z faktury. Zkontrolujte a případně upravte.</p>
            </div>
            <div>
              <Label>Zakázka (návrh, nepovinné)</Label>
              <Select value={form.suggested_project_id} onValueChange={(v) => setForm({ ...form, suggested_project_id: v })}>
                <SelectTrigger className="h-11 mt-1" data-testid="invoice-project"><SelectValue placeholder="— vyberte stavbu —" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">Konečné přiřazení k zakázce provede stavbyvedoucí při schválení.</p>
            </div>
            <div><Label>Poznámka (nepovinné)</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="h-11 mt-1" placeholder="např. obklady koupelna 2. NP" /></div>
            <Button onClick={submit} disabled={saving || ocring} className="w-full h-12 font-bold" data-testid="invoice-submit">
              {saving ? "Odesílám…" : ocring ? "Načítám fakturu…" : "Odeslat ke schválení"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
